import { Add, Close, Save } from '@mui/icons-material';
import { Box, Button, Grid, useMediaQuery, FormControlLabel, Switch } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { deepEqual } from 'fast-equals';
import { useSnackbar } from 'notistack';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import {
  Loader,
  Page,
  PageTitleWithBack,
  SIDENAV_WIDTH_COLLAPSED,
  SIDENAV_WIDTH_EXPANDED
} from '../../../components';
import { ExternalLink } from '../../../components/link/ExternalLink';
import { SideNavContext } from '../../../context';
import {
  getCustomNavigationDetail,
  getCustomPage,
  updateCustomNavigation,
  updateCustomPage,
  createCustomNavigation
} from '../../../fetch';
import {
  ICustomNavigationUpdate,
  ICustomPageDetail,
  ICustomPageSection,
  ICustomSubNavigationDetail
} from '../../../models';
import { theme } from '../../../styles/theme';
import { EditSections } from './EditSections';
import { NavigationSection } from './NavigationSection';

export const DEFAULT_SECTION = {
  name: 'Section Title',
  customPageSectionId: null,
  parentCustomPageSectionId: null,
  text: '',
  subText: '',
  imageText: '',
  imageUrl: '',
  linkText: '',
  linkUrl: '',
  sections: []
};

const NUM_SECTIONS_TO_LOAD = -1;

export const ManageCustomPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const { showNav } = useContext(SideNavContext);
  const isMobile = useMediaQuery(`(max-width: 900px)`);
  const sideNavWidth = useMemo(() => {
    if (showNav) {
      // Show Icons and Labels
      return SIDENAV_WIDTH_EXPANDED;
    } else if ((!showNav && !isMobile) || (showNav && isMobile)) {
      // Only show Icons
      return SIDENAV_WIDTH_COLLAPSED;
    } else {
      // Hide completely
      return 0;
    }
  }, [showNav, isMobile]);
  const classes = useStyles({ sideNavWidth, isMobile });
  const [customPage, setCustomPage] = useState<ICustomPageDetail | null>(null);
  const [isActivePage, setIsActive] = useState<boolean>(true);
  const [selectedNavType, setSelectedNavType] = useState<string>('None');
  const [customPageCopy, setCustomPageCopy] = useState<ICustomPageDetail | null>(null);
  const [sections, setSections] = useState<ICustomPageSection[]>([]);
  const [customNavigation, setCustomNavigation] = useState<ICustomSubNavigationDetail | null>(null);
  const [customSubNavigation, setCustomSubNavigation] = useState<ICustomSubNavigationDetail[]>([]);
  const [isSaving, setSaving] = useState<boolean>(false);
  const [isLoadingPage, setLoadingPage] = useState<boolean>(false);
  const [isLoadingNav, setLoadingNav] = useState<boolean>(false);
  const [isEditingNavItem, setEditingNavItem] = useState<{
    [id: number]: boolean;
  }>({});
  const location = useLocation();
  const split = location.pathname.split('/');
  const slug = split[split.length - 1];

  const fetchPage = async () => {
    try {
      setLoadingPage(true);
      const data = await getCustomPage(slug, NUM_SECTIONS_TO_LOAD);
      if (data.customNavigationId) {
        await fetchNav(data.customNavigationId);
      }
      setCustomPage(data);
      setIsActive(data.isActive);
      setCustomPageCopy(data);
      setSections(data.sections);
      setSelectedNavType(data.navigationType);
    } catch (e) {
      console.log(e);
    } finally {
      setLoadingPage(false);
    }
  };

  const fetchNav = async navId => {
    try {
      setLoadingNav(true);
      const navData = await getCustomNavigationDetail(navId);
      setCustomNavigation(navData);
      setCustomSubNavigation(
        navData.subNavigation.map((sub, index) => ({ ...sub, sortOrder: index + 1 }))
      );
    } catch (e) {
      console.log(e);
    } finally {
      setLoadingNav(false);
    }
  };

  const saveNavigation = async () => {
    // clean array and set temp ids to null for new created ones and drop the flag for telling
    // if an item is a new one
    const subNavData =
      customSubNavigation?.map(sn => {
        if (sn.isNew) {
          sn.customNavigationId = null;
          delete sn.isNew;
        }
        return {
          ...sn,
          subNavigation: sn.subNavigation.map(subSn => {
            if (subSn.isNew) {
              subSn.customNavigationId = null;
              delete subSn.isNew;
            }
            return subSn;
          })
        };
      }) ?? [];
    try {
      if (!customPage.customNavigationId) {
        const createNavRes = await createCustomNavigation({
          customNavigationId: null,
          name: customPage.name,
          url: '',
          openNewTab: false,
          sortOrder: 0,
          subNavigation: subNavData
        });
        if (createNavRes?.Detail) {
          setSaving(false);
          return enqueueSnackbar(`${createNavRes.Detail}`, {
            variant: 'error'
          });
        }
        return createNavRes;
      } else if (customPage.customNavigationId) {
        // make sure we have an id before we try and save the custom navigation
        const navRes = await updateCustomNavigation(customPage.customNavigationId, {
          ...customNavigation,
          subNavigation: subNavData
        } as ICustomNavigationUpdate);

        if (navRes?.Detail) {
          setSaving(false);
          return enqueueSnackbar(`${navRes.Detail}`, {
            variant: 'error'
          });
        }
      }
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    fetchPage();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isInNavEditMode = Object.values(isEditingNavItem)?.filter(Boolean)?.length > 0;
  const isSaveDisabled = isInNavEditMode
    ? true
    : deepEqual(customPage, {
        ...customPageCopy,
        sections,
        isActive: isActivePage,
        navigationType: selectedNavType
      }) && deepEqual(customNavigation ?? [], customSubNavigation);
  const pageName = customPage ? `Manage ${customPage?.name}` : 'Loading...';
  return (
    <Page title={pageName}>
      {isSaving && <Loader position="centered" type="fullscreen" />}
      <PageTitleWithBack
        backText="Back to custom pages"
        title={pageName}
        backUrl="/admin/manage-content/custom-pages"
        subTitle={
          customPage && (
            <>
              <ExternalLink
                target="_blank"
                to={`${window.location.origin}/custom-pages/${customPage.slug}`}
              >
                /custom-pages/{customPage.slug}
              </ExternalLink>
            </>
          )
        }
        additionalHeaderContent={
          !isLoadingPage && (
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={isActivePage}
                  id="active-inactive"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                    setIsActive(checked);
                  }}
                />
              }
              label="Active"
            />
          )
        }
      />
      {isLoadingPage && <Loader position="centered" />}
      {!isLoadingPage && customPage && (
        <Box className={classes.flexContainer}>
          <Box className={classes.contentArea}>
            <Grid container spacing={1}>
              <Grid item xs={12} md={4}>
                {isLoadingNav && (
                  <Box marginTop="16px">
                    <Loader position="centered" type="inline" />
                  </Box>
                )}
                {!isLoadingNav && (
                  <NavigationSection
                    isEditing={isEditingNavItem}
                    setEditing={setEditingNavItem}
                    customNavigation={customSubNavigation}
                    setCustomNavigation={setCustomSubNavigation}
                    setSelectedNavType={setSelectedNavType}
                    selectedNavType={selectedNavType}
                  />
                )}
              </Grid>
              <Grid item xs={12} md={8}>
                <EditSections
                  sections={sections}
                  setSections={setSections}
                  customPageId={customPage?.customPageId ?? -1}
                />
              </Grid>
            </Grid>
          </Box>
          <Box className={classes.saveWrapper}>
            <Box className={classes.buttonWrapper}>
              <Box flex={1}>
                <Box marginRight="8px" display="inline-block" className={classes.saveButtonWrapper}>
                  <Button
                    type="button"
                    startIcon={<Save />}
                    variant="contained"
                    color="primary"
                    disabled={isSaveDisabled}
                    onClick={async () => {
                      try {
                        setSaving(true);

                        const customNavId = await saveNavigation();
                        const res = await updateCustomPage(customPage.customPageId, {
                          ...customPage,
                          customNavigationId: customPage.customNavigationId ?? customNavId ?? null,
                          isActive: isActivePage,
                          navigationType: selectedNavType,
                          sections
                        });
                        if (res.status >= 400 || res?.data?.Detail) {
                          return enqueueSnackbar(
                            res?.data?.Detail ||
                              (res.status === 403 && 'Not Authorized') ||
                              `Error saving custom page, please try again.`,
                            {
                              variant: 'error'
                            }
                          );
                        }
                        // reset the state so that the button is no longer dirty
                        setCustomNavigation({
                          ...customNavigation,
                          subNavigation: customSubNavigation
                        });
                        setCustomPage({
                          ...customPage,
                          sections
                        });
                        setCustomPageCopy({
                          ...customPage,
                          sections
                        });
                        enqueueSnackbar('Custom page updated!', {
                          variant: 'success'
                        });
                      } catch (error) {
                        enqueueSnackbar(`Error saving custom page, please try again.`, {
                          variant: 'error'
                        });
                        console.log(error);
                      } finally {
                        setSaving(false);
                      }
                    }}
                  >
                    Save
                  </Button>
                </Box>
                <Button
                  type="button"
                  variant="contained"
                  startIcon={<Close />}
                  color="inherit"
                  onClick={() => {
                    history.push('/admin/manage-content/custom-pages');
                  }}
                >
                  Cancel
                </Button>
              </Box>
              <Button
                color="secondary"
                variant="contained"
                startIcon={<Add />}
                onClick={() => {
                  setSections([
                    ...sections,
                    {
                      ...DEFAULT_SECTION,
                      type: 'GenericSection',
                      sortOrder: sections.length + 1
                    }
                  ]);
                  // this will scroll to the bottom of the page where the new section was created
                  setTimeout(() => {
                    window.scrollTo(0, document.body.scrollHeight);
                    const el = document.getElementById(`section-${sections.length}`);
                    if (el) {
                      // on the fly change the box shadow to highlight the new one created
                      (
                        el.children[0] as HTMLDivElement
                      ).style.boxShadow = `0px 0px 1px 1px ${theme.palette.primary.main}`;
                      setTimeout(() => {
                        // after 2 seconds remove the custom box shadow
                        (el.children[0] as HTMLDivElement).style.boxShadow = `none`;
                      }, 2000);
                    }
                  }, 200);
                }}
              >
                Add Section
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Page>
  );
};

const useStyles = makeStyles<Theme, { sideNavWidth: number; isMobile: boolean }>(theme => ({
  flexContainer: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 'calc(100vh - 345px)'
  },
  contentArea: {
    flex: 1,
    overflowY: 'auto',
    paddingTop: '8px'
  },
  saveWrapper: {
    borderTop: `1px solid ${theme.palette.primary.main}`,
    padding: ({ isMobile }) => (isMobile ? theme.spacing(0.75) : theme.spacing(0.5)),
    backgroundColor: '#fff',
    borderRadius: 2,
    boxShadow:
      '0px -2px 4px -1px rgb(0 0 0 / 20%),0px 4px 5px 0px rgba(0,0,0,0.14%), 0px 0px 0px 0px rgb(0 0 0 / 0%)',
    marginBottom: ({ isMobile }) => (isMobile ? '-30px' : '-12px'),
    '@media (max-width: 900px)': {
      width: 'calc(100% + 50px)',
      margin: '0 -25px',
      boxSizing: 'border-box'
    }
  },
  saveButtonWrapper: {
    width: '100%',
    '& button': {
      width: '100%',
      marginTop: '.5rem'
    },
    '@media (min-width: 900px)': {
      width: 'auto',
      '& button': {
        width: 'auto',
        marginTop: 0
      }
    }
  },
  buttonWrapper: {
    display: 'flex',
    flexDirection: 'column',
    '& button': {
      width: '100%',
      marginTop: '.5rem'
    },
    '@media (min-width: 900px)': {
      flexDirection: 'row',
      '& button': {
        width: 'auto',
        marginTop: 0
      }
    }
  }
}));
